import React from "react";
import type { SectionCfg, SectionOptions } from "../types";
import {
  useCreatorData,
  useCreatorSectionLoading,
  useTemplateSectionInstance,
} from "../templateData";
import { E, useEdit } from "../InlineEdit";
import { Shell, type SectionProps } from "../TemplateSections";
import { useShopCatalog } from "../useShopCatalog";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import {
  mergeShopBrandOptions,
  productsForBrand,
} from "../shopPageMerch";
import { useMerchBandCatalogGate } from "../ShopInlineFeaturedContext";
import { useSectionNetworkCascade } from "../templateSectionNetworkCascade";
import { SHOP_BROWSE_OTHER_SLUG } from "../shopBreadcrumbs";
import { findCarouselBySlug } from "../recentContentCarouselOptions";
import { useAboutMoreFromCardImages } from "../useAboutMoreFromCardImages";
import type { FeaturedCarouselItem } from "../types";
import { FeaturedBand } from "./FeaturedBand";
import {
  buildFeaturedCategorySlots,
  buildFeaturedCollectionSlots,
  mergeFeaturedCategoryCoverPool,
  resolveFeaturedCategorySlugs,
} from "./buildFeaturedBandSlots";
import {
  brandSlugsFromCollectionKeys,
  collectionSlotKind,
  parseFeaturedBrandSlugs,
  parseShopBrandSlug,
  systemCollectionLabel,
} from "./featuredCollectionKeys";
import {
  buildFeaturedBrandImages,
  useFeaturedBrandCovers,
} from "./useFeaturedBrandCovers";
import { useFeaturedCategoryCovers } from "./useFeaturedCategoryCovers";
import {
  featuredBandTemplateForCount,
  normalizeFeaturedBandCount,
  normalizeFeaturedCollectionCount,
  type FeaturedBandImage,
  type FeaturedBandImageMode,
  type FeaturedBandSlot,
} from "./featuredBandTypes";
import {
  applySlotImageOverrides,
  parseSlotImages,
} from "./featuredBandSlotImages";

function resolveImageMode(cfg?: SectionCfg): FeaturedBandImageMode {
  return String(cfg?.imageMode || "single").toLowerCase() === "grid"
    ? "grid"
    : "single";
}

function withModesAndImages(
  slots: FeaturedBandSlot[],
  cfg: SectionCfg | undefined,
  mode: FeaturedBandImageMode,
  opts?: { defaultFit?: "contain" | "cover" },
): FeaturedBandSlot[] {
  const raw = cfg?.slotImageModes;
  const parts = Array.isArray(raw)
    ? raw.map((s) => String(s).toLowerCase())
    : String(raw || "")
        .split(",")
        .map((s) => s.trim().toLowerCase());
  const withModes = slots.map((slot, i) => ({
    ...slot,
    mode: ((parts[i] || mode) === "grid"
      ? "grid"
      : "single") as FeaturedBandImageMode,
  }));
  return applySlotImageOverrides(
    withModes,
    parseSlotImages(cfg, withModes.length || 1),
    opts,
  );
}

function parseSelectedKeys(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean);
  }
  return String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function FeaturedBandSectionShell({
  T,
  cfg,
  emptyHint,
  slots,
  loading,
  naturalHeight = false,
  onSlotClick,
  onImageClick,
}: SectionProps & {
  emptyHint: string;
  slots: FeaturedBandSlot[];
  loading: boolean;
  /** Featured Collections: contain-center images in the fixed square frame. */
  naturalHeight?: boolean;
  onSlotClick?: (slot: FeaturedBandSlot, index: number) => void;
  onImageClick?: (
    slot: FeaturedBandSlot,
    image: FeaturedBandImage,
    imageIndex: number,
    slotIndex: number,
  ) => void;
}) {
  const { editable, get } = useEdit();
  const sectionPath = useTemplateSectionInstance();
  const count = normalizeFeaturedBandCount(
    cfg?.count ?? (slots.length || 1),
  );
  const template = featuredBandTemplateForCount(count);
  const device =
    T.device === "tablet" || T.device === "mobile" ? T.device : "desktop";
  const compact = device !== "desktop";
  // Section title — same kicker typography as Content Featured Outfit.
  const headingFallback = String(cfg?.heading || "").trim();
  const headingEdit = sectionPath
    ? get(`section.${sectionPath}.heading`, "\u0000")
    : "\u0000";
  const headingCleared = headingEdit === "";
  const liveHeading =
    headingEdit === "\u0000"
      ? headingFallback
      : String(headingEdit || "").trim();
  // Surface only (no bg/surface toggle): nest inside ProductGrid which already
  // applies the page gutter, so fullBleed keeps L/R edges on the result column.
  const { heading: _omitHeading, ...restCfg } = cfg || {};
  const shellCfg = { ...restCfg, bgTone: "surface" as const };

  // Keep the kicker after an inline clear (required title) so layout doesn't
  // jump; don't invent a blank kicker when the section never had a title.
  const titleEl =
    liveHeading || (editable && headingCleared) ? (
    <p className="mte-tpl-looks-kicker mte-fb-section-kicker">
      {sectionPath ? (
        <E path={`section.${sectionPath}.heading`} placeholder="Add title">
          {liveHeading}
        </E>
      ) : (
        liveHeading
      )}
    </p>
  ) : null;

  if (loading) {
    const naturalCls = naturalHeight ? " mte-fb-band--natural" : "";
    const isCard = template.layout === "card";
    const stackMulti = isCard && count > 1;
    // Match live layout: Split stacks two column-filling squares. Hero stays
    // beside the title. Trio/Quad = tile frames + caption lines.
    const skel =
      isCard ? (
        <div
          className={`mte-fb-band${count > 1 ? " mte-fb-band--multi" : ""}${
            stackMulti ? " mte-fb-band--stack mte-fb-band--stack-2" : ""
          }${naturalCls}`}
          aria-hidden="true"
        >
          {Array.from({ length: count }).map((_, i) => (
            <div
              key={i}
              className={`mte-fb-card${
                compact || stackMulti ? " mte-fb-card--stacked" : ""
              }${compact ? " mte-fb-card--mobile" : ""}`}
              style={{
                height: "auto",
                flex: stackMulti ? "none" : count > 1 ? "1 1 0" : undefined,
                minWidth: 0,
              }}
            >
              {compact || stackMulti ? (
                <>
                  <div
                    className="mte-fb-strip mte-fb-strip--full"
                    style={{ width: "100%" }}
                  >
                    <div
                      className="mte-fb-strip-img"
                      style={{
                        width: "100%",
                        aspectRatio: "1",
                        height: "auto",
                      }}
                    >
                      <span className="mte-fb-fill mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
                    </div>
                  </div>
                  <div className="mte-fb-card-text">
                    <span
                      className="mte-tpl-skel-line mte-tpl-skel-line--lg"
                      style={{ width: "9rem", display: "block", marginBottom: 8 }}
                    />
                    <span
                      className="mte-tpl-skel-line mte-tpl-skel-line--sm"
                      style={{ width: "4rem", display: "block" }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="mte-fb-strip">
                    <div
                      className="mte-fb-strip-img"
                      style={{
                        width: "100%",
                        maxWidth: "none",
                        maxHeight: "none",
                        height: "auto",
                        aspectRatio: "1",
                        flex: "none",
                      }}
                    >
                      <span className="mte-fb-fill mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
                    </div>
                  </div>
                  <div className="mte-fb-card-text mte-fb-card-text--left">
                    <span
                      className="mte-tpl-skel-line mte-tpl-skel-line--lg"
                      style={{ width: "9rem", display: "block", marginBottom: 8 }}
                    />
                    <span
                      className="mte-tpl-skel-line mte-tpl-skel-line--sm"
                      style={{ width: "4rem", display: "block" }}
                    />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div
          className={`mte-fb-band mte-fb-band--multi${naturalCls}`}
          aria-hidden="true"
        >
          {Array.from({ length: count }).map((_, i) => (
            <div
              key={i}
              className="mte-fb-tile"
              style={{ flex: "1 1 0", minWidth: 0 }}
            >
              <div className="mte-fb-frame mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
              <div className="mte-fb-below">
                <span
                  className="mte-tpl-skel-line mte-tpl-skel-line--md"
                  style={{ width: "70%", display: "block", marginBottom: 6 }}
                />
                <span
                  className="mte-tpl-skel-line mte-tpl-skel-line--sm"
                  style={{ width: "28%", display: "block" }}
                />
              </div>
            </div>
          ))}
        </div>
      );

    return (
      <Shell
        T={T}
        cfg={shellCfg}
        fullBleed
        independentShellClassName="mte-fb-shell"
      >
        {titleEl}
        {skel}
      </Shell>
    );
  }

  if (!slots.length) {
    if (!editable) return null;
    return (
      <Shell
        T={T}
        cfg={shellCfg}
        fullBleed
        independentShellClassName="mte-fb-shell"
      >
        {titleEl}
        <p className="mte-fb-empty" role="status">
          {emptyHint}
        </p>
      </Shell>
    );
  }

  return (
    <Shell
      T={T}
      cfg={shellCfg}
      fullBleed
      independentShellClassName="mte-fb-shell"
    >
      {titleEl}
      <FeaturedBand
        slots={slots.slice(0, count)}
        template={template}
        device={device}
        naturalHeight={naturalHeight}
        onSlotClick={editable ? undefined : onSlotClick}
        onImageClick={editable ? undefined : onImageClick}
      />
    </Shell>
  );
}

/** Products featured category band (Hero → Quad). */
export function FeaturedCategoriesSection({
  T,
  cfg,
  onNavigate,
  isActive = true,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const cascade = useSectionNetworkCascade();
  const cascadeSectionId =
    String(sectionPath || "")
      .split("/")
      .pop()
      ?.trim() || "";
  const cascadeAllowsFetch =
    !cascade || !cascadeSectionId || cascade.canFetch(cascadeSectionId);
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);
  const count = normalizeFeaturedBandCount(cfg?.count ?? 1);
  const mode = resolveImageMode(cfg);
  const template = featuredBandTemplateForCount(count);

  const { products, browseIndex, isBrowseCatalogSettled, primaryCoverBySlug } = useShopCatalog({
    username,
    userId,
    enabled: hasSession && isActive && cascadeAllowsFetch,
    shopView: "all",
    categorySlug: null,
    shopBrowse: {},
    surface: "products",
    // Load the product grid so tile covers match Add/ghost (facet sample alone
    // left grid cells blank or repeated one sample vs configure's product picks).
  });

  const showSkel = useCreatorSectionLoading(
    Boolean(products.length || browseIndex.primaries.length),
  );

  const tabsFromBrowse = React.useMemo(() => {
    // Categories only from breadcrumb primaries — never content_category tabs.
    return browseIndex.primaries
      .filter(
        (p) => p.slug !== SHOP_BROWSE_OTHER_SLUG && String(p.slug || "").trim(),
      )
      .map((p) => ({
        slug: p.slug,
        label: p.label,
        count: p.count ?? 0,
      }));
  }, [browseIndex.primaries]);

  const selectedCategorySlugs = React.useMemo(() => {
    const raw = cfg?.selectedSlugs;
    const selected = Array.isArray(raw)
      ? raw.map((s) => String(s).trim()).filter(Boolean)
      : String(raw || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    return resolveFeaturedCategorySlugs(selected, tabsFromBrowse, count);
  }, [cfg?.selectedSlugs, tabsFromBrowse, count]);

  // Same RPC filter as Products → Women's (etc.) so tile #1 matches grid #1.
  const categoryCovers = useFeaturedCategoryCovers({
    username,
    userId,
    enabled: hasSession && isActive && cascadeAllowsFetch,
    slugs: selectedCategorySlugs,
  });

  // Hold the default product catalog until this band can paint the category
  // covers (same rows as that category's browse page). Facet samples and the
  // unfiltered grid arrive first and are different products on large catalogs.
  // After the first settle, category edits must NOT flip loading — that
  // re-gates the Products grid and looks like a full page reload.
  const bandSettledOnceRef = React.useRef(false);
  const bandLoading =
    hasSession &&
    cascadeAllowsFetch &&
    !bandSettledOnceRef.current &&
    (categoryCovers.loading ||
      (tabsFromBrowse.length === 0 &&
        (showSkel || !isBrowseCatalogSettled)));
  if (
    hasSession &&
    cascadeAllowsFetch &&
    !bandLoading &&
    !categoryCovers.loading
  ) {
    bandSettledOnceRef.current = true;
  }
  useMerchBandCatalogGate(
    Boolean(
      isActive &&
        cascadeAllowsFetch &&
        (bandSettledOnceRef.current || !bandLoading),
    ),
  );

  React.useEffect(() => {
    if (!cascade || !cascadeSectionId || !isActive || !cascadeAllowsFetch) return;
    if (!bandLoading) cascade.reportSettled(cascadeSectionId);
  }, [
    cascade,
    cascadeSectionId,
    isActive,
    cascadeAllowsFetch,
    bandLoading,
  ]);

  const slots = React.useMemo(() => {
    // keepPreviousData leaves prior cover RPC rows until the new slug set
    // returns — always merge the browse catalog so a first footwear→home
    // click paints Home immediately (Update was dirty but canvas stayed put).
    const pool = mergeFeaturedCategoryCoverPool({
      coverProducts: categoryCovers.products,
      coverBySlug: categoryCovers.coverBySlug,
      catalogProducts: products,
      primaryCoverBySlug,
      coversLoading: categoryCovers.loading,
    });
    return buildFeaturedCategorySlots({
      tabs: tabsFromBrowse,
      products: pool.products,
      count,
      mode,
      maxImages: template.maxImages,
      selectedSlugs: cfg?.selectedSlugs as string | string[] | undefined,
      coverBySlug: pool.coverBySlug,
      coverUrlsBySlug: categoryCovers.coverUrlsBySlug,
    });
  }, [
    tabsFromBrowse,
    products,
    categoryCovers.products,
    categoryCovers.loading,
    categoryCovers.coverBySlug,
    categoryCovers.coverUrlsBySlug,
    count,
    mode,
    template.maxImages,
    cfg?.selectedSlugs,
    primaryCoverBySlug,
  ]);

  const slotsWithModes = React.useMemo(
    () => withModesAndImages(slots, cfg, mode, { defaultFit: "contain" }),
    [slots, cfg, mode],
  );

  const demoSlots: FeaturedBandSlot[] =
    !hasSession && !slotsWithModes.length
      ? [
          {
            id: "demo-beauty",
            title: "Beauty",
            mode,
            images: [{ src: CREATOR.products[0]?.image || "" }],
          },
          {
            id: "demo-home",
            title: "Home",
            mode,
            images: [{ src: CREATOR.products[1]?.image || "" }],
          },
        ].slice(0, count)
      : slotsWithModes;

  return (
    <FeaturedBandSectionShell
      T={T}
      cfg={cfg}
      emptyHint="Pick categories in the editor to preview this band."
      slots={demoSlots}
      // Match Featured Collections: contain-center product tiles (not cover zoom).
      naturalHeight
      // Hold both bands until browse settles so Categories doesn't appear
      // after Collections (order flash). Public shows null — not a gray block.
      // Do not tie to products grid `loading` — that re-skeletons on refetch /
      // breadcrumb remount even when browse facets are already cached.
      loading={bandLoading}
      onSlotClick={(slot) => {
        if (editable || !onNavigate || !slot.slug) return;
        const image = String(slot.images?.[0]?.src || "").trim();
        analytics?.trackSectionEvent(
          TEMPLATE_ANALYTICS_EVENTS.FEATURED_CAROUSEL_CLICK,
          {
            sectionId: "featuredCategories",
            sectionType: "FeaturedCategories",
            entityType: "product",
            entityId: slot.slug,
            entityLabel: slot.title,
            details: {
              kind: "category",
              slug: slot.slug,
              image,
              name: slot.title,
            },
          },
        );
        analytics?.trackShopBrowse({
          level: "primary",
          label: slot.title || slot.slug,
          primarySlug: slot.slug,
          secondarySlug: null,
          subSlug: null,
          brandSlug: null,
        });
        onNavigate("shop", {
          shopView: "all",
          shopBrowse: {
            primarySlug: slot.slug,
            secondarySlug: null,
            subSlug: null,
            brandSlug: null,
          },
        });
      }}
    />
  );
}

FeaturedCategoriesSection.options = {
  heading: {
    kind: "text",
    label: "Title",
    default: "",
    placeholder: "Enter a title",
  },
} as SectionOptions;

/** Featured collections band — system collections + creator collections. */
export function FeaturedCollectionsSection({
  T,
  cfg,
  onNavigate,
  isActive = true,
}: SectionProps) {
  const CREATOR = useCreatorData();
  const { editable } = useEdit();
  const analytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const cascade = useSectionNetworkCascade();
  const cascadeSectionId =
    String(sectionPath || "")
      .split("/")
      .pop()
      ?.trim() || "";
  const cascadeAllowsFetch =
    !cascade || !cascadeSectionId || cascade.canFetch(cascadeSectionId);
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const hasSession = !!(username && userId);
  const catalogOn = hasSession && isActive && cascadeAllowsFetch;
  const count = normalizeFeaturedCollectionCount(cfg?.count ?? 1);
  const mode = resolveImageMode(cfg);
  const template = featuredBandTemplateForCount(count);
  const featured = CREATOR.featuredCarousel;

  const shop = useShopCatalog({
    username,
    userId,
    enabled: catalogOn,
    shopView: "all",
    categorySlug: null,
    shopBrowse: {},
    surface: "products",
    skipGrid: true,
  });
  const trending = useShopCatalog({
    username,
    userId,
    enabled: catalogOn,
    shopView: "trending",
    categorySlug: null,
    shopBrowse: {},
    surface: "products",
  });

  // Prefer Recent Content / shop carousels (with `coverImage` / card_image) so
  // selected collection keys resolve even when they aren't Featured Carousel
  // members. Featured members fill any gaps.
  const sourceItems = React.useMemo(() => {
    const byKey = new Map<string, FeaturedCarouselItem>();
    const put = (item: FeaturedCarouselItem) => {
      const slug = String(item.slug || "").trim();
      const id = String(item.id || "").trim();
      if (slug && !byKey.has(slug)) byKey.set(slug, item);
      if (id && !byKey.has(id)) byKey.set(id, item);
    };
    for (const c of CREATOR.carousels || []) {
      const slug = String(c.renderSlug || "").trim();
      if (!slug) continue;
      const cover =
        String(c.coverImage || "").trim() ||
        c.items?.find((it) => String(it.image || "").trim())?.image ||
        "";
      put({
        id: String(c.stayComponentRowId || c.id || slug),
        title: c.title || "Collection",
        image: cover,
        slug,
        kind: (c.type === "shop" ? "shop" : "content") as "shop" | "content",
        stayComponentRowId: c.stayComponentRowId,
      });
    }
    for (const it of featured?.items || []) put(it);
    return Array.from(
      new Map(
        [...byKey.values()].map((it) => [
          String(it.slug || it.id || "").trim(),
          it,
        ]),
      ).values(),
    );
  }, [featured?.items, CREATOR.carousels]);

  const collectionCoverSeed = React.useMemo(
    () =>
      sourceItems.map((it) => ({
        title: it.title || "Collection",
        body: "",
        image: String(it.image || "").trim(),
        stayComponentRowId: it.stayComponentRowId,
      })),
    [sourceItems],
  );
  const { items: syncedCollectionCovers, isSyncing: collectionCoversSyncing } =
    useAboutMoreFromCardImages(
      collectionCoverSeed,
      userId,
      username,
      catalogOn,
    );
  const sourceItemsWithLiveCovers = React.useMemo(() => {
    const byRow = new Map(
      syncedCollectionCovers.map((c) => [
        String(c.stayComponentRowId ?? "").trim(),
        String(c.image ?? "").trim(),
      ]),
    );
    return sourceItems.map((it) => {
      const rowId = String(it.stayComponentRowId ?? "").trim();
      const synced = rowId ? byRow.get(rowId) : "";
      if (!synced) return it;
      return { ...it, image: synced };
    });
  }, [sourceItems, syncedCollectionCovers]);

  const selectedKeys = React.useMemo(
    () => parseSelectedKeys(cfg?.selectedIds ?? cfg?.selectedSlugs),
    [cfg?.selectedIds, cfg?.selectedSlugs],
  );

  const brandSlugsForCovers = React.useMemo(() => {
    const keys =
      selectedKeys.length > 0
        ? selectedKeys
        : Array.from({ length: count }, (_, i) => sourceItems[i]?.slug || "");
    return brandSlugsFromCollectionKeys(keys.slice(0, count));
  }, [selectedKeys, count, sourceItems]);

  // Per-brand fetches so one popular brand can't fill the whole cover page.
  const brandCovers = useFeaturedBrandCovers({
    username,
    userId,
    enabled: catalogOn && brandSlugsForCovers.length > 0,
    slugs: brandSlugsForCovers,
  });

  const productPool = React.useMemo(() => {
    const byId = new Map<string, (typeof shop.catalogProducts)[number]>();
    const ordered: (typeof shop.catalogProducts)[number][] = [];
    const push = (p: (typeof shop.catalogProducts)[number]) => {
      const id = String(p.postId || "").trim();
      if (id) {
        if (byId.has(id)) return;
        byId.set(id, p);
      }
      ordered.push(p);
    };
    // Brand-page rows first so a later unfiltered catalog page cannot
    // replace tile #1 with a different product of the same brand.
    for (const p of brandCovers.products) push(p);
    for (const p of shop.catalogProducts) push(p);
    return ordered;
  }, [shop.catalogProducts, brandCovers.products]);

  const brandCoverBySlug = React.useMemo(() => {
    const map = new Map<string, string>();
    // Skip catalog/facet samples while the brand-page covers are still in
    // flight — those samples are what flash, then get replaced.
    if (!brandCovers.loading) {
      for (const [slug, img] of shop.brandCoverBySlug) {
        if (slug && img && !brandCovers.coverBySlug.has(slug)) map.set(slug, img);
      }
    }
    for (const [slug, img] of brandCovers.coverBySlug) {
      if (slug && img) map.set(slug, img);
    }
    return map;
  }, [shop.brandCoverBySlug, brandCovers.coverBySlug, brandCovers.loading]);

  const brands = React.useMemo(
    () =>
      mergeShopBrandOptions(shop.browseIndex, productPool, brandCoverBySlug),
    [shop.browseIndex, productPool, brandCoverBySlug],
  );
  const brandLabel = React.useCallback(
    (slug: string) => brands.find((b) => b.slug === slug)?.label || slug,
    [brands],
  );

  const slots = React.useMemo(() => {
    const keys =
      selectedKeys.length > 0
        ? selectedKeys
        : Array.from(
            { length: count },
            (_, i) => sourceItemsWithLiveCovers[i]?.slug || "",
          );
    const ownSlots = buildFeaturedCollectionSlots({
      items: sourceItemsWithLiveCovers,
      count: keys.length || count,
      mode,
      maxImages: template.maxImages,
      selectedIds: keys.filter((k) => collectionSlotKind(k) === "collection"),
    });
    const ownByKey = new Map<string, FeaturedBandSlot>();
    for (const slot of ownSlots) {
      if (slot.slug) ownByKey.set(slot.slug, slot);
      ownByKey.set(slot.id, slot);
    }

    return keys.slice(0, count).map((key, i) => {
      const kind = collectionSlotKind(key);
      if (kind === "trending") {
        const imgs = trending.products
          .map((p) => String(p.image || "").trim())
          .filter(Boolean)
          .slice(0, 4);
        return {
          id: key,
          title: "Trending products",
          slug: key,
          mode,
          images: Array.from({ length: 4 }, (_, j) => ({
            src: imgs[j] || imgs[0] || "",
            alt: "Trending",
          })),
        };
      }
      if (kind === "shop-brand") {
        const slug = parseShopBrandSlug(key);
        const fromBrandPage = productsForBrand(brandCovers.products, slug).slice(0, 4);
        const products = fromBrandPage.length
          ? fromBrandPage
          : brandCovers.loading
            ? []
            : productsForBrand(productPool, slug).slice(0, 4);
        const brand = brands.find((b) => b.slug === slug);
        const fallback = brandCovers.loading ? [] : brand?.images || [];
        const title = systemCollectionLabel(key, brandLabel);
        return {
          id: key,
          title,
          slug: key,
          mode,
          images: Array.from({ length: 4 }, (_, j) => ({
            src: String(
              products[j]?.image ||
                products[0]?.image ||
                fallback[j] ||
                fallback[0] ||
                "",
            ).trim(),
            alt: title,
          })),
        };
      }
      if (kind === "featured-brands") {
        const slugs = parseFeaturedBrandSlugs(key);
        return {
          id: key,
          title: "Featured brands",
          slug: key,
          mode,
          images: buildFeaturedBrandImages({
            slugs,
            brands,
            productPool: brandCovers.products.length
              ? brandCovers.products
              : brandCovers.loading
                ? []
                : productPool,
            coverBySlug: brandCoverBySlug,
          }),
        };
      }
      const own = ownByKey.get(key);
      if (own) return { ...own, mode };
      return {
        id: key || `col-${i}`,
        title: "Collection",
        slug: key,
        mode,
        images: Array.from({ length: 4 }, () => ({ src: "" })),
      };
    });
  }, [
    selectedKeys,
    count,
    sourceItemsWithLiveCovers,
    mode,
    template.maxImages,
    trending.products,
    productPool,
    brandCovers.products,
    brandCovers.loading,
    brands,
    brandLabel,
    brandCoverBySlug,
  ]);

  const slotsWithModes = React.useMemo(
    () => withModesAndImages(slots, cfg, mode, { defaultFit: "contain" }),
    [slots, cfg, mode],
  );

  const showSkel = useCreatorSectionLoading(
    Boolean(slotsWithModes.length) || shop.hasAllProducts,
  );

  const visibleCollectionKeys = React.useMemo(() => {
    if (selectedKeys.length > 0) return selectedKeys.slice(0, count);
    return sourceItems
      .slice(0, count)
      .map((it) => String(it.slug || "").trim())
      .filter(Boolean);
  }, [selectedKeys, count, sourceItems]);
  const wantsTrending = visibleCollectionKeys.some(
    (key) => collectionSlotKind(key) === "trending",
  );
  const wantsOwnCollection = visibleCollectionKeys.some(
    (key) => collectionSlotKind(key) === "collection",
  );

  // Match FeaturedCategories: wait for browse settle so Collections don't
  // flash empty. Also wait for brand-page / own-collection covers so a
  // catalog sample cannot paint and then swap. Once those covers have
  // settled, never re-skeleton / re-gate Products on slug edits.
  // Top→bottom: Collections wait on Categories via SectionNetworkCascade.
  const bandSettledOnceRef = React.useRef(false);
  const bandLoading =
    hasSession &&
    cascadeAllowsFetch &&
    !bandSettledOnceRef.current &&
    ((brandSlugsForCovers.length > 0 && brandCovers.loading) ||
      (wantsTrending && trending.loading) ||
      (wantsOwnCollection && collectionCoversSyncing) ||
      (!slotsWithModes.length &&
        (showSkel || !shop.isBrowseCatalogSettled)));
  if (
    hasSession &&
    cascadeAllowsFetch &&
    !bandLoading &&
    !(brandSlugsForCovers.length > 0 && brandCovers.loading) &&
    !(wantsTrending && trending.loading) &&
    !(wantsOwnCollection && collectionCoversSyncing)
  ) {
    bandSettledOnceRef.current = true;
  }

  // Hold the default product catalog until this band can paint.
  useMerchBandCatalogGate(
    Boolean(
      isActive &&
        cascadeAllowsFetch &&
        (bandSettledOnceRef.current || !bandLoading),
    ),
  );

  React.useEffect(() => {
    if (!cascade || !cascadeSectionId || !isActive || !cascadeAllowsFetch) return;
    if (!bandLoading) cascade.reportSettled(cascadeSectionId);
  }, [
    cascade,
    cascadeSectionId,
    isActive,
    cascadeAllowsFetch,
    bandLoading,
  ]);

  return (
    <FeaturedBandSectionShell
      T={T}
      cfg={cfg}
      emptyHint="Pick system or your collections in the editor to preview this band."
      slots={slotsWithModes}
      loading={bandLoading || (hasSession && isActive && !cascadeAllowsFetch)}
      naturalHeight
      onSlotClick={(slot) => {
        if (editable || !onNavigate || !slot.slug) return;
        const key = slot.slug;
        const kind = collectionSlotKind(key);
        const image = String(slot.images?.[0]?.src || "").trim();
        analytics?.trackSectionEvent(
          TEMPLATE_ANALYTICS_EVENTS.FEATURED_CAROUSEL_CLICK,
          {
            sectionId: "featuredCollections",
            sectionType: "FeaturedCollections",
            entityType: "carousel",
            entityId: key,
            entityLabel: slot.title,
            details: {
              kind: "collection",
              slotKind: kind,
              key,
              image,
              name: slot.title,
            },
          },
        );
        if (kind === "trending") {
          analytics?.trackShopTab({
            tabKind: "trending",
            label: slot.title || "Trending",
          });
          onNavigate("shop", { shopView: "trending" });
          return;
        }
        if (kind === "shop-brand") {
          const brandSlug = parseShopBrandSlug(key);
          if (brandSlug) {
            analytics?.trackShopBrowse({
              level: "brand",
              label: slot.title || brandSlug,
              brandSlug,
            });
            onNavigate("shop", {
              shopView: "brand",
              shopBrowse: { brandSlug },
            });
          }
          return;
        }
        if (kind === "featured-brands") {
          const brandSlugs = parseFeaturedBrandSlugs(key);
          if (brandSlugs.length) {
            analytics?.trackShopBrowse({
              level: "brand",
              label: slot.title || brandSlugs[0] || "Brands",
              brandSlug: brandSlugs[0] || null,
            });
            onNavigate("shop", {
              shopView: "brand",
              shopBrowse: {
                brandSlugs,
                brandSlug: brandSlugs[0] || null,
              },
            });
          }
          return;
        }
        const item = sourceItems.find(
          (it) => it.slug === key || String(it.id) === key,
        );
        const car = findCarouselBySlug(CREATOR.carousels, key);
        // All collection slots: shop type → Products, otherwise → Content.
        const page: "shop" | "content" =
          car?.type === "shop" || (!car && item?.kind === "shop")
            ? "shop"
            : "content";
        const navSlug = String(car?.renderSlug || key).trim() || key;
        if (page === "content") {
          onNavigate("content", { carouselSlug: navSlug });
        } else {
          onNavigate("shop", {
            shopView: "all",
            shopBrowse: {},
            carouselSlug: navSlug,
          });
        }
      }}
      onImageClick={(slot, image) => {
        if (editable || !onNavigate) return;
        const brandSlug = String(image.brandSlug || "").trim().toLowerCase();
        if (!brandSlug) return;
        if (collectionSlotKind(slot.slug || "") !== "featured-brands") return;
        analytics?.trackSectionEvent(
          TEMPLATE_ANALYTICS_EVENTS.FEATURED_CAROUSEL_CLICK,
          {
            sectionId: "featuredCollections",
            sectionType: "FeaturedCollections",
            entityType: "product",
            entityId: brandSlug,
            entityLabel: image.label || brandSlug,
            details: {
              kind: "featured-brand-cell",
              brandSlug,
              image: String(image.src || "").trim(),
              name: image.label || brandSlug,
            },
          },
        );
        analytics?.trackShopBrowse({
          level: "brand",
          label: image.label || brandSlug,
          brandSlug,
        });
        onNavigate("shop", {
          shopView: "brand",
          shopBrowse: { brandSlug },
        });
      }}
    />
  );
}

FeaturedCollectionsSection.options = {
  heading: {
    kind: "text",
    label: "Title",
    default: "",
    placeholder: "Enter a title",
  },
} as SectionOptions;
